import React, {useState} from "react";
import {Vector2d} from "konva/types/types";
import {KonvaEventObject} from "konva/types/Node";
import {BaseStageConfig, KonvaEventType, PointDragState, SnappingState} from "../../types";
import {getAngleSnapThreshold, getPointerPosition, getSnappedPosition, setStageCursor} from "../../utils";
import {CursorStyle} from "../../enums";
import ScaleLine from "./ScaleLine";
import ScalePoint from "./ScalePoint";
import {Group, Rect} from "react-konva";

const BaseScaleLayer: React.FC<{
	listening?: boolean
	addScalePoint: (position: Vector2d) => void
	updateScalePointPosition?: (index: number, position: Vector2d) => void
	scalePoints: Vector2d[]
	snapping?: SnappingState,
	stageConfig: BaseStageConfig,
	panInProgress: boolean
}> = ({
	listening = false,
	addScalePoint,
	updateScalePointPosition,
	scalePoints,
	snapping,
	stageConfig,
	panInProgress
}) => {
	const [pointerLocation, setPointerLocation] = useState<Vector2d | null>(null);
	const [pointDragState, setPointDragState] = useState<PointDragState>({isDragging: false});

	function handleMouseUp(event: KonvaEventObject<KonvaEventType>) {
		if (panInProgress) return;
		const position = getPointerPosition(event);
		if (position) {
			if (scalePoints.length === 0) {
				addScalePoint(position)
			}
			if (scalePoints.length === 1) {
				addScalePoint(snapping?.angle
					? getSnappedPosition(scalePoints[0], position, getAngleSnapThreshold(snapping))
					: position);
				setStageCursor(event, CursorStyle.DEFAULT)
			}
		}
	}

	function handleMouseMove(event: KonvaEventObject<MouseEvent>) {
		if (panInProgress) return;
		if (scalePoints.length < 2) {
			setStageCursor(event, CursorStyle.CROSSHAIR)
		}
		const position = getPointerPosition(event);
		if (position) {
			if (scalePoints.length === 0) {
				setPointerLocation(position);
			}
			if (scalePoints.length === 1) {
				setPointerLocation(snapping?.angle
					? getSnappedPosition(scalePoints[0], position, getAngleSnapThreshold(snapping))
					: position)
			}
		}
	}

	function handlePointDragStart(evt: KonvaEventObject<DragEvent>, index: number) {
		const mousePosition = getPointerPosition(evt);
		if (mousePosition) {
			let snappedPosition = mousePosition;
			if (snapping?.angle && scalePoints.length === 2) {
				const anchorIndex = index === 0 ? 1 : 0;
				snappedPosition = getSnappedPosition(scalePoints[anchorIndex], mousePosition, getAngleSnapThreshold(snapping));
			}
			setPointDragState({
				isDragging: true,
				dragPointId: String(index),
				dragPointCurrentLocation: snappedPosition
			});
		}
		setStageCursor(evt, CursorStyle.MOVE);
	}

	function handlePointDragMove(evt: KonvaEventObject<DragEvent>, index: number) {
		const mousePosition = getPointerPosition(evt);
		if (mousePosition) {
			let snappedPosition = mousePosition;
			if (snapping?.angle && scalePoints.length === 2) {
				const anchorIndex = index === 0 ? 1 : 0;
				snappedPosition = getSnappedPosition(scalePoints[anchorIndex], mousePosition, getAngleSnapThreshold(snapping));
			}
			setPointDragState(prevState => ({
				...prevState,
				dragPointCurrentLocation: snappedPosition
			}));
		}
	}

	function handlePointDragEnd(evt: KonvaEventObject<DragEvent>, index: number) {
		setStageCursor(evt, CursorStyle.DEFAULT);
		const mousePosition = getPointerPosition(evt);
		if (mousePosition) {
			let finalPosition: Vector2d = mousePosition;
			if (snapping?.angle && scalePoints.length === 2) {
				const anchorIndex = index === 0 ? 1 : 0;
				finalPosition = getSnappedPosition(scalePoints[anchorIndex], mousePosition, getAngleSnapThreshold(snapping));
			}
			if (updateScalePointPosition) {
				updateScalePointPosition(index, finalPosition);
			}
			// Reset the Konva node position to the snapped position
			evt.target.position({x: finalPosition.x, y: finalPosition.y});
		}
		setPointDragState({isDragging: false});
	}

	function handlePointMouseEnter(evt: KonvaEventObject<MouseEvent>) {
		if (scalePoints.length === 2) {
			setStageCursor(evt, CursorStyle.MOVE);
		}
	}

	function handlePointMouseLeave(evt: KonvaEventObject<MouseEvent>) {
		if (!pointDragState.isDragging) {
			setStageCursor(evt, CursorStyle.DEFAULT);
		}
	}

	function getDisplayPoints(): Vector2d[] {
		return scalePoints.map((point, i) => {
			if (pointDragState.isDragging &&
				pointDragState.dragPointId === String(i) &&
				pointDragState.dragPointCurrentLocation) {
				return pointDragState.dragPointCurrentLocation;
			}
			return point;
		});
	}

	function renderScalePoints() {
		const canDrag = scalePoints.length === 2 && !!updateScalePointPosition;
		const displayPoints = getDisplayPoints();

		return displayPoints.map((point, i) => (
			<ScalePoint
				key={i}
				index={i}
				center={point}
				draggable={canDrag}
				onDragStart={handlePointDragStart}
				onDragMove={handlePointDragMove}
				onDragEnd={handlePointDragEnd}
				onMouseEnter={handlePointMouseEnter}
				onMouseLeave={handlePointMouseLeave}
			/>
		));
	}

	function renderScaleLine() {
		if (scalePoints.length) {
			let points: number[] | null = null;
			const displayPoints = getDisplayPoints();

			if (displayPoints.length === 1 && pointerLocation) {
				points = [displayPoints[0].x, displayPoints[0].y, pointerLocation.x, pointerLocation.y];
			}
			if (displayPoints.length >= 2) {
				points = [displayPoints[0].x, displayPoints[0].y, displayPoints[1].x, displayPoints[1].y]
			}
			return points ? <ScaleLine points={points}/> : null;
		}
		return null;
	}

	return (
		<Group listening={listening} onMouseUp={handleMouseUp} onTouchEnd={handleMouseUp} onMouseMove={handleMouseMove}>
			<Rect name={"background_rect_scale"} width={stageConfig.width} height={stageConfig.height}/>
			{renderScaleLine()}
			{renderScalePoints()}
		</Group>
	);
};

export {BaseScaleLayer}
